import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchPaylinkCustomersQuery } from '../store/modules/merchant/api';
import {
  paylinkCustomerSortableFields,
  SearchPaylinkCustomersQuery,
} from '../store/modules/merchant/types';
import { TableParams } from '../common/Customers/CustomersTable';
import { orderByValues } from '../store/modules/paylink/types';

interface Props {
  merchantId: string;
}

type SearchParams = Omit<SearchPaylinkCustomersQuery, 'merchantId'>;
type FilterParams = Pick<SearchParams, 'searchQuery' | 'customerId' | 'cardOnFile'>;

const parseSearchParamNumber = (value: string | null, defaultValue: number) =>
  Number.isNaN(Number(value)) ? defaultValue : Number(value);

export const useCustomersTable = ({ merchantId }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams({
    pageNumber: '1',
    pageSize: '10',
    sortBy: 'firstName',
    orderBy: 'asc',
  } satisfies { [key in keyof SearchParams]?: string });

  const getSearchParam = useCallback(
    (key: keyof SearchParams) => {
      return searchParams.get(key);
    },
    [searchParams],
  );

  const updateSearchParam = useCallback(
    (key: keyof SearchParams, value?: string | null) => {
      setSearchParams((prev) => {
        if (value && value.length > 0) prev.set(key, value);
        else prev.delete(key);
        return prev;
      });
    },
    [setSearchParams],
  );

  const tableParams: TableParams = useMemo(
    () => ({
      pageNumber: parseSearchParamNumber(getSearchParam('pageNumber'), 1),
      pageSize: parseSearchParamNumber(getSearchParam('pageSize'), 10),
      sortBy:
        paylinkCustomerSortableFields.find((f) => f === getSearchParam('sortBy')) ?? 'firstName',
      orderBy: orderByValues.find((order) => order === getSearchParam('orderBy')) ?? 'asc',
    }),
    [getSearchParam],
  );

  const customerIdParam = getSearchParam('customerId');
  const filterParams: FilterParams = useMemo(
    () => ({
      searchQuery: getSearchParam('searchQuery') ?? undefined,
      customerId:
        customerIdParam && customerIdParam.length > 0 ? customerIdParam.split(',') : undefined,
      cardOnFile: ['true', 'false'].includes(getSearchParam('cardOnFile') ?? '')
        ? getSearchParam('cardOnFile') === 'true'
        : undefined,
    }),
    [customerIdParam, getSearchParam],
  );

  const { data: customerData, isFetching } = useSearchPaylinkCustomersQuery(
    {
      merchantId,
      ...tableParams,
      ...filterParams,
    },
    { skip: !merchantId },
  );

  return {
    customerData,
    isLoading: isFetching,
    tableParams,
    setTableParams: ({ pageNumber, pageSize, sortBy, orderBy }: TableParams) => {
      updateSearchParam('pageNumber', pageNumber.toString());
      updateSearchParam('pageSize', pageSize.toString());
      updateSearchParam('sortBy', sortBy);
      updateSearchParam('orderBy', orderBy);
    },
    filterParams,
    setFilterParams: ({ searchQuery, customerId, cardOnFile }: FilterParams) => {
      updateSearchParam('searchQuery', searchQuery);
      updateSearchParam('customerId', customerId?.join(','));
      if (cardOnFile) {
        updateSearchParam('cardOnFile', 'true');
      } else {
        // Setting it to false would hide the customers with a card on file and we just want to
        // clear the filter
        updateSearchParam('cardOnFile', null);
      }
    },
  };
};
